<template>
    <page ref="page" :busy="busy" :tabs="tabs">
        <div v-if="!isNil(subscription)">
            <b-alert show variant="danger" class="ml-2 mr-2 mt-3 mb-3" v-if="subscription.is_suspended" dismissible>
                <div class="mb-2">
                    <i
                        >Your Lodgix subscription is currently suspended due to a problem with your last payment. Please
                        update your credit card details below and click the "Pay Now" button to restore access to your
                        Lodgix account.</i
                    >
                </div>
                <div>
                    <i
                        >If you wish to cancel your subscription, please click on the "Cancel Lodgix" button and your
                        subscription will be immediately cancelled.</i
                    >
                </div>
            </b-alert>
            <grid
                ref="grid"
                :column-defs="columnDefs"
                :is-auto-height="true"
                :is-search="false"
                :row-data="subscriptionRows"
                :get-row-id="getRowId"
                :pinned-bottom-row-data="pinnedBottomRowData"
                :get-row-style="getRowStyle"
                :get-row-height="getRowHeight"
                :is-auto-width="true"
                :suppress-row-hover-highlight="true"
                class="mt-2"
            >
            </grid>
            <div class="main-subscription-buttons" v-if="!isPayNow">
                <b-button variant="default" @click="handleAddModules" v-if="showAddModules"
                    ><font-awesome-icon class="mr-2" :icon="['fas', 'plus']" size="lg" /> Add Modules</b-button
                >
                <b-button variant="default" @click="toggleShowBillingDetails">Edit Billing Details</b-button>
            </div>

            <br />
            <div class="ml-3 mb-3" v-if="isPayNow">
                Credit card will be billed <b>{{ subscription.total }}</b> on <b>{{ subscription.trial_to }}</b>
            </div>
            <subscription-credit-card-form
                :subscription="subscription"
                @success-card-saved="handleSuccessCardSaved"
                @success-coupon-applied="handleSuccessCouponApplied"
                v-show="showBillingDetails || isPayNow"
            />
            <p v-if="isPayNow" class="ml-3 small">
                Please call 1-877-593-2083 or email <a href="mailto:support@lodgix.com">support@lodgix.com</a> if you
                have any questions, concerns or feedback.
            </p>
        </div>
        <subscription-cancel-modal
            title="Cancel Subscription Confirm"
            :modal-id="subscriptionCancelModalId"
            @success-cancel="handleCancelSubscriptionSuccess"
        />
        <subscription-module-change-modal title="Module Subscription" :modal-id="subscriptionModuleChangeModalId" />
        <modal :id="subscriberModuleErrorModalId" title="Error" ok-only
            >To access the {{ missingModuleMapping[missingModule] }} functionality, you must click "Add Modules" and add
            the {{ missingModuleMapping[missingModule] }} Module to your monthly invoice.
        </modal>
    </page>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import isEmpty from 'lodash/isEmpty';
    import includes from 'lodash/includes';
    import page from '../../generic/page/page.vue';
    import API from '../../../api';
    import Modal from '../../generic/modal/modal.vue';
    import grid from '../../generic/grid/grid.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { SUBSCRIPTION_BILLING_DATA_ENDPOINT, SUBSCRIPTION_BILLING_SAVE_CARD_ENDPOINT } from '../../../endpoints';
    import SubscriptionCancelModal from './subscription-cancel-modal.vue';
    import SubscriptionModuleChangeModal from './subscription-module-change-modal.vue';
    import SubscriptionCreditCardForm from './subscription-credit-card-form.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';

    export default {
        components: {
            page,
            grid,
            Modal,
            ResponsiveButton,
            SubscriptionCancelModal,
            SubscriptionModuleChangeModal,
            SubscriptionCreditCardForm,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        active: true,
                        title: 'Billing',
                        route: '/app/subscription/billing/',
                    },
                    {
                        active: false,
                        title: 'Activity',
                        route: '/app/subscription/activity/',
                    },
                ],
                subscription: null,
                subscriptionCard: null,
                couponsAllowed: false,
                trialEnds: null,
                totalAmount: 0,
                nextBillingDate: null,
                subscriptionCancelModalId: 'subscription-cancel-modal',
                subscriptionModuleChangeModalId: 'subscription-module-change-modal',
                subscriberModuleErrorModalId: 'subscriber-module-error-modal',
                showBillingDetails: false,
                missingModule: null,
                missingModuleMapping: {
                    property_management: 'Property Management',
                    quickbooks: 'Quickbooks',
                },
            };
        },
        computed: {
            ...mapGetters('header', ['userIsTeamMember']),
            isPending() {
                return this.subscription.status === 'CREATED';
            },
            isPayNow() {
                return this.subscription.status === 'CREATED' || this.subscription.is_suspended;
            },
            showAddModules() {
                return includes(['ACTIVE', 'GRACE', 'TRIAL'], this.subscription.status);
            },
            columnDefs() {
                return [
                    {
                        headerName: 'Status',
                        field: 'status',
                        cellRenderer: ({ value }) => {
                            if (value) {
                                return !this.isPending ? `<b>${value}</b>` : '<b>PENDING</b>';
                            }
                        },
                        minWidth: 150,
                        maxWidth: 200,
                        sortable: false,
                    },
                    {
                        headerName: 'Package',
                        field: 'package',
                        minWidth: 150,
                        sortable: false,
                    },
                    {
                        headerName: 'Description',
                        field: 'description',
                        sortable: false,
                        autoWidthExclude: true,
                    },
                    {
                        headerName: 'Signup Date',
                        field: 'signup_date',
                        minWidth: 300,
                        cellRenderer: ({ data, value, node }) => {
                            if (!node.rowPinned) {
                                if (!data.is_scheduled_for_removal && !this.subscription.package_scheduled_for_change) {
                                    return value;
                                } else if (data.module && data.is_scheduled_for_removal) {
                                    return `Scheduled for removal on ${this.subscription.next_billing_date}`;
                                } else if (
                                    this.subscription.package_scheduled_for_change &&
                                    !(data.module && data.is_scheduled_for_removal)
                                ) {
                                    return `Scheduled for downgrade on  ${this.subscription.next_billing_date}`;
                                }
                            }
                            return value;
                        },
                        sortable: false,
                    },
                    {
                        headerName: 'Next Billing Date',
                        field: 'next_billing_date',
                        minWidth: 140,
                        cellRenderer: ({ data, value }) => {
                            return !data.is_scheduled_for_removal && !this.subscription.package_scheduled_for_change
                                ? value
                                : '';
                        },
                        sortable: false,
                    },
                    {
                        headerName: 'Trial End',
                        field: 'trial_to',
                        minWidth: 140,
                        sortable: false,
                        hide: this.subscription.trial_expired,
                    },
                    {
                        headerName: 'Amount',
                        field: 'amount',
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        cellRenderer: ({ data, value }) => {
                            return !data.is_scheduled_for_removal && !this.subscription.package_scheduled_for_change
                                ? value
                                : '';
                        },
                        sortable: false,
                    },
                    {
                        headerName: '',
                        minWidth: 150,
                        maxWidth: 150,
                        resizable: false,
                        sortable: false,
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            subscription: this.subscription,
                            onCancel: async () => {
                                this.$bvModal.show(this.subscriptionCancelModalId);
                            },
                            onModuleChange: async () => {
                                this.$bvModal.show(this.subscriptionModuleChangeModalId);
                            },
                        },
                        sortable: false,
                    },
                ];
            },
            subscriptionRows() {
                return !isNil(this.subscription?.package)
                    ? [
                          {
                              status: this.subscription.status,
                              package: this.subscription.package.name,
                              description: this.subscription.package.description,
                              signup_date: this.subscription.started,
                              next_billing_date: this.subscription.next_billing_date,
                              trial_to: this.subscription.trial_to,
                              amount: this.subscription.package.amount,
                              is_scheduled_for_removal: false,
                              module: false,
                              component: null,
                          },
                      ].concat(
                          this.subscription.modules.map((module) => ({
                              status: '',
                              package: module.name,
                              description: module.description,
                              signup_date: this.subscription.started,
                              next_billing_date: this.subscription.next_billing_date,
                              trial_to: this.subscription.trial_to,
                              amount: module.amount,
                              is_scheduled_for_removal: module.is_scheduled_for_removal,
                              module: true,
                              component: module.component,
                          }))
                      )
                    : [];
            },
            pinnedBottomRowData() {
                return [
                    {
                        status: '',
                        next_billing_date: this.nextBillingDate,
                        trial_to: this.trialEnds,
                        amount: this.totalAmount,
                    },
                ];
            },
        },
        methods: {
            isNil,
            async fetchSuscriptionBillingData() {
                try {
                    this.busy = true;
                    const response = await API.get(SUBSCRIPTION_BILLING_DATA_ENDPOINT());

                    this.subscription = response.data;
                    this.couponsAllowed = false;
                    if (this.subscription.package != null) {
                        this.couponsAllowed = this.subscription.package.coupons_allowed;
                    }
                    this.trialEnds = this.subscription.trial_to;
                    this.nextBillingDate = this.subscription.next_billing_date;
                    this.totalAmount = this.subscription.total;
                } catch (e) {
                    this.errorToast({ text: 'Could not load subscription billing data.' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAccount() {
                try {
                    await this.$store.dispatch('header/fetchAccount');
                    await this.$store.dispatch('header/fetchNotifications');
                } catch (err) {
                    console.log(`Could not fetch account data in header nav bar: ${err}`);
                }
            },
            getRowId(item) {
                return item.data.package;
            },
            getRowHeight() {
                return 45;
            },
            handleAddModules() {
                this.$bvModal.show(this.subscriptionModuleChangeModalId);
            },
            handleCancelSubscriptionSuccess() {
                this.$store.state.subscription.showCancelationMessage = true;
                this.$router.push('/app/subscription/select');
            },
            handleSuccessCardSaved() {
                this.fetchAccount();
                this.fetchSuscriptionBillingData();
                if (!this.subscription.is_suspended) {
                    this.successToast({ text: 'Credit Card sucessfully saved.' });
                }
            },
            handleSuccessCouponApplied() {
                this.successToast({ text: 'Promotion Code sucessfully applied.' });
                this.fetchSuscriptionBillingData();
            },
            toggleShowBillingDetails() {
                this.showBillingDetails = !this.showBillingDetails;
            },
            showModuleWarning(module) {
                this.missingModule = module;
                this.$bvModal.show(this.subscriberModuleErrorModalId);
            },
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        mounted() {
            if (!isEmpty(this.$route.query)) {
                this.showModuleWarning(this.$route.query['module']);
                this.$router.push(this.$route.path);
            }
            this.fetchSuscriptionBillingData();
        },
    };
</script>

<style scoped lang="scss">
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.alert) {
        font-size: 14px;
    }

    :deep(.main-subscription-buttons) {
        margin-left: 1rem;
        margin-right: 0.1rem;
        margin-top: 1rem;

        .btn {
            width: 160px;
            margin-left: 0;
            margin-right: 0.5rem;
            margin-bottom: 10px;
        }
    }

    @include media-breakpoint-down(sm) {
        :deep(.main-subscription-buttons) {
            margin-left: 1rem;
            margin-right: 1rem;
            margin-top: 1rem;

            .btn {
                width: 100%;
                margin-bottom: 10px;
            }
        }
    }
</style>
